import { useEffect, useState } from "react";

import {
    Badge,
    Button,
    Card,
    CardBody,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Input
} from "reactstrap";

import { MoreVertical } from "lucide-react";

import { ChatMessage } from "../types/ChatMessage";
import { MessageReaction } from "../types/Reaction";

import AuthHelper from "../../../../common/auth/service/AuthHelper";
import MessageReactionService from "../service/MessageReactionService";
import "./MessageBubble.css";

interface Props {

    message: ChatMessage;
reactionRefresh: number;
    onReply: (message: ChatMessage) => void;

    onEdit: (
        messageId: string,
        message: string
    ) => Promise<void>;

    onDelete: (
        messageId: string
    ) => Promise<void>;

}

const MessageBubble = ({
    message,
    reactionRefresh,
    onReply,
    onEdit,
    onDelete
}: Props) => {

    const [menuOpen, setMenuOpen] =
        useState(false);

    const [editing, setEditing] =
        useState(false);

    const [editedMessage, setEditedMessage] =
        useState(message.message);

    const [reactions, setReactions] =
        useState<MessageReaction[]>([]);

    const isMine =
        message.senderId ===
        AuthHelper.getUserId();

  useEffect(() => {

    loadReactions();

}, [
    message.chatMessageId,
    reactionRefresh
]);

    const loadReactions = async () => {

        try {

            const response =
                await MessageReactionService.getReactions(
                    message.chatMessageId
                );

            setReactions(response);

        }
        catch (error) {

            console.error(error);

        }

    };

    const react = async (
        emoji: string
    ) => {

        try {

            await MessageReactionService.addReaction(
                message.chatMessageId,
                emoji
            );

            await loadReactions();

        }
        catch (error) {

            console.error(error);

        }

    };

    const saveEdit = async () => {

        if (!editedMessage.trim())
            return;

        await onEdit(
            message.chatMessageId,
            editedMessage
        );

        setEditing(false);

    };

    return (

       <div
  className={`chat-message-row ${
    isMine ? "my-message" : "other-message"
  }`}
>
  <div className="chat-bubble">

    {/* Header */}
    <div className="chat-message-header">

      <strong className="chat-sender-name">
        {isMine ? "You" : message.senderName}
      </strong>

      <Dropdown
        isOpen={menuOpen}
        toggle={() => setMenuOpen(!menuOpen)}
      >
        <DropdownToggle
          color="link"
          className="chat-menu-btn"
        >
          <MoreVertical size={17} />
        </DropdownToggle>

        <DropdownMenu end>

          <DropdownItem
            onClick={() => onReply(message)}
          >
            ↩️ Reply
          </DropdownItem>

          {isMine && (
            <>
              <DropdownItem
                onClick={() => setEditing(true)}
              >
                ✏️ Edit
              </DropdownItem>

              <DropdownItem
                className="text-danger"
                onClick={() =>
                  onDelete(message.chatMessageId)
                }
              >
                🗑️ Delete
              </DropdownItem>
            </>
          )}

        </DropdownMenu>
      </Dropdown>

    </div>


    {/* Reply Preview */}
    {message.replyToMessage && (
      <div className="chat-reply-preview">

        <div className="chat-reply-line"></div>

        <div className="chat-reply-content">

          <strong>
            {message.replyToUserName}
          </strong>

          <span>
            {message.replyToMessage}
          </span>

        </div>

      </div>
    )}


    {/* Message */}
    <div className="chat-message-content">

      {editing ? (
        <div className="chat-edit-box">

          <Input
            value={editedMessage}
            onChange={(e) =>
              setEditedMessage(e.target.value)
            }
            className="chat-edit-input"
          />

          <div className="chat-edit-actions">

            <Button
              color="success"
              size="sm"
              onClick={saveEdit}
            >
              Save
            </Button>

            <Button
              color="secondary"
              size="sm"
              onClick={() => {
                setEditing(false);
                setEditedMessage(message.message);
              }}
            >
              Cancel
            </Button>

          </div>

        </div>
      ) : (
        <span className="chat-text">
          {message.message}
        </span>
      )}

    </div>


    {/* Bottom */}
    <div className="chat-message-bottom">

      <span className="chat-time">
        {new Date(
          message.sentAt
        ).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })}
      </span>

      {message.isEdited && (
        <span className="chat-edited">
          Edited
        </span>
      )}

      {isMine && (
        <span className="chat-read">
          ✓✓
        </span>
      )}

    </div>


    {/* Reaction Buttons */}
    <div className="chat-reaction-picker">

      <button
        type="button"
        onClick={() => react("👍")}
      >
        👍
      </button>

      <button
        type="button"
        onClick={() => react("❤️")}
      >
        ❤️
      </button>

      <button
        type="button"
        onClick={() => react("😂")}
      >
        😂
      </button>

      <button
        type="button"
        onClick={() => react("🎉")}
      >
        🎉
      </button>

    </div>


    {/* Reactions */}
    {reactions.length > 0 && (
      <div className="chat-reactions">

        {reactions.map((reaction) => (
          <span
            key={reaction.messageReactionId}
            className="chat-reaction-badge"
            title={reaction.userName}
          >
            {reaction.reaction}
            {reactions.length > 1 && (
              <small>{reaction.userName}</small>
            )}
          </span>
        ))}

      </div>
    )}

  </div>
</div>

    );

};

export default MessageBubble;